import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Loader2, Pencil, Plus, Trash2, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Label from '../components/ui/Label';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import RoleSelector from '../components/RoleSelector';
import { HEADER_SELECT_CLASS, PageHeaderActions } from '../components/PageHeaderActions';
import { useAuth } from '../contexts/AuthContext';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS, membershipsOf } from '../services/permissions';
import { getRoleCatalog, orgMembersService, roleLabel } from '../services/rolesService';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';

const DEFAULT_NEW_MEMBER_ROLES = ['operator'];

function apiErrorText(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

/**
 * Org admins manage who is in the selected organization and what each person's roles allow.
 * Everything here is scoped to the org in the header pulldown; the backend requires ``org.users``
 * there. Platform admins see the same page for any org they belong to.
 */
function OrganizationMembers() {
  const { user } = useAuth();
  const { can, orgId } = usePermissions();
  const canManage = can(PERMISSIONS.ORG_USERS);

  // Orgs where this user may manage members drive the header pulldown.
  const manageableOrgs = membershipsOf(user)
    .filter((m) => user?.is_admin || (m.permissions || []).includes(PERMISSIONS.ORG_USERS))
    .map((m) => ({ id: String(m.organization_id), name: m.organization_name }));

  const [catalog, setCatalog] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', first_name: '', last_name: '', roles: DEFAULT_NEW_MEMBER_ROLES });
  const [adding, setAdding] = useState(false);

  const [editing, setEditing] = useState(null); // member being edited
  const [editRoles, setEditRoles] = useState([]);
  const [savingRoles, setSavingRoles] = useState(false);

  const [removing, setRemoving] = useState(null); // member pending removal
  const [removeBusy, setRemoveBusy] = useState(false);

  useEffect(() => {
    getRoleCatalog().then(setCatalog).catch(() => setCatalog({ roles: [], permissions: [] }));
  }, []);

  const load = useCallback(async () => {
    if (!orgId || !canManage) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setMembers(await orgMembersService.list(orgId));
    } catch (err) {
      setError(apiErrorText(err, 'Failed to load members.'));
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, canManage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const sync = () => load();
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, sync);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, sync);
  }, [load]);

  const handleOrganizationChange = (value) => {
    const parsed = organizationsService.parseOrganizationOptionValue(value);
    const nextId = parsed ? parsed.id : value;
    organizationsService.setSelectedOrganization(nextId, 'id', manageableOrgs);
  };

  const orgName = manageableOrgs.find((o) => o.id === String(orgId))?.name || '';

  const submitAdd = async () => {
    const email = addForm.email.trim();
    if (!email) {
      toast.error('Enter an email address.');
      return;
    }
    if (addForm.roles.length === 0) {
      toast.error('Pick at least one role.');
      return;
    }
    setAdding(true);
    try {
      const created = await orgMembersService.add(orgId, {
        email,
        first_name: addForm.first_name.trim(),
        last_name: addForm.last_name.trim(),
        roles: addForm.roles,
      });
      setMembers((prev) => [...prev.filter((m) => m.user_id !== created.user_id), created]);
      setAddOpen(false);
      setAddForm({ email: '', first_name: '', last_name: '', roles: DEFAULT_NEW_MEMBER_ROLES });
      toast.success(`${created.email} added to ${orgName}`);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to add member.'));
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (member) => {
    setEditing(member);
    setEditRoles(member.roles || []);
  };

  const submitRoles = async () => {
    if (!editing) return;
    setSavingRoles(true);
    try {
      const updated = await orgMembersService.setRoles(orgId, editing.user_id, editRoles);
      setMembers((prev) => prev.map((m) => (m.user_id === updated.user_id ? updated : m)));
      setEditing(null);
      toast.success('Roles updated');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to update roles.'));
    } finally {
      setSavingRoles(false);
    }
  };

  const confirmRemove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    try {
      await orgMembersService.remove(orgId, removing.user_id);
      setMembers((prev) => prev.filter((m) => m.user_id !== removing.user_id));
      toast.success(`${removing.email} removed from ${orgName}`);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to remove member.'));
    } finally {
      setRemoveBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visible = members.filter((m) => {
    if (!q) return true;
    return [m.first_name, m.last_name, m.email].some((v) => (v || '').toLowerCase().includes(q));
  });

  return (
    <div className="space-y-6">
      <PageHeaderActions>
        <FunctionalSelect
          label="Organization"
          className={HEADER_SELECT_CLASS}
          value={organizationsService.getSelectedOptionValue(manageableOrgs, orgId, 'id')}
          onValueChange={handleOrganizationChange}
          placeholder="Select organization"
          disabled={manageableOrgs.length === 0}
        >
          {manageableOrgs.map((org) => (
            <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
              {org.name}
            </SelectItem>
          ))}
        </FunctionalSelect>
      </PageHeaderActions>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>
              {orgName ? `${orgName} members` : 'Members'}
              {!loading && members.length > 0 ? (
                <span className="ml-2 text-sm font-normal text-zinc-500">({members.length})</span>
              ) : null}
            </CardTitle>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setAddOpen(true)}
              disabled={!canManage || !orgId}
              aria-label="Add member"
              title="Add member"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full"
            aria-label="Search members"
          />
          {!canManage ? (
            <p className="text-sm text-amber-700">Your role in this organization does not allow managing members.</p>
          ) : loading ? (
            <p className="text-sm text-gray-500">Loading members…</p>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : visible.length === 0 ? (
            <p className="text-sm text-gray-500">{q ? 'No members match your search.' : 'No members yet.'}</p>
          ) : (
            <div className="border border-gray-200 rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Name</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Email</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Roles</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Status</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((m) => {
                    const isSelf = m.user_id === user?.id;
                    return (
                      <tr key={m.user_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-[#18181B]">
                          {[m.first_name, m.last_name].filter(Boolean).join(' ') || '—'}
                          {isSelf ? <span className="ml-1 text-xs text-zinc-400">(you)</span> : null}
                        </td>
                        <td className="px-3 py-2 text-zinc-600">{m.email}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {m.is_platform_admin ? (
                              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Platform Admin</span>
                            ) : null}
                            {(m.roles || []).length === 0 && !m.is_platform_admin ? (
                              <span className="text-xs text-amber-700">No roles — cannot see anything</span>
                            ) : null}
                            {(m.roles || []).map((r) => (
                              <span key={r} className="rounded-full bg-[#E8EEF5] px-2 py-0.5 text-xs text-[#18181B]">
                                {roleLabel(catalog, r)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs ${
                              m.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {m.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEdit(m)}
                              disabled={isSelf && !user?.is_admin}
                              className="rounded p-1 text-[#0062FB] hover:bg-blue-50 disabled:opacity-40"
                              aria-label="Edit roles"
                              title={isSelf && !user?.is_admin ? 'You cannot change your own roles' : 'Edit roles'}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setRemoving(m)}
                              disabled={isSelf}
                              className="rounded p-1 text-[#F53D7A] hover:bg-pink-50 disabled:opacity-40"
                              aria-label="Remove from organization"
                              title={isSelf ? 'You cannot remove yourself' : 'Remove from organization'}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Modal isOpen={addOpen} onClose={() => !adding && setAddOpen(false)} className="w-full max-w-2xl mx-4">
        <CardContent className="space-y-4 pt-6">
          <h2 className="pr-8 text-lg font-semibold text-[#18181B]">Add a member to {orgName}</h2>
          <p className="text-sm text-zinc-500">
            An existing account is linked to this organization. A new email creates the account and sends an invite with a temporary password.
          </p>
          <div className="space-y-2">
            <Label htmlFor="member-email">Email</Label>
            <Input
              id="member-email"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="person@company.com"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="member-first">First name</Label>
              <Input id="member-first" value={addForm.first_name} onChange={(e) => setAddForm((f) => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="member-last">Last name</Label>
              <Input id="member-last" value={addForm.last_name} onChange={(e) => setAddForm((f) => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Roles in {orgName} (pick at least one)</Label>
            <RoleSelector
              catalog={catalog}
              value={addForm.roles}
              onChange={(roles) => setAddForm((f) => ({ ...f, roles }))}
              idPrefix="add-role"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button variant="outline" onClick={submitAdd} disabled={adding} className="gap-1.5">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              Add member
            </Button>
          </div>
        </CardContent>
      </Modal>

      <Modal isOpen={Boolean(editing)} onClose={() => !savingRoles && setEditing(null)} className="w-full max-w-2xl mx-4">
        {editing ? (
          <CardContent className="space-y-4 pt-6">
            <h2 className="pr-8 text-lg font-semibold text-[#18181B]">
              Roles for {[editing.first_name, editing.last_name].filter(Boolean).join(' ') || editing.email}
            </h2>
            <p className="text-sm text-zinc-500">
              Applies only in {orgName}. With several roles, the person gets everything any of them allows.
            </p>
            <RoleSelector
              catalog={catalog}
              value={editRoles}
              onChange={setEditRoles}
              idPrefix="edit-role"
            />
            {editRoles.length === 0 ? (
              <p className="text-sm text-red-600">Pick at least one role. Without one this person cannot see anything in {orgName}.</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditing(null)} disabled={savingRoles}>
                Cancel
              </Button>
              <Button variant="outline" onClick={submitRoles} disabled={savingRoles || editRoles.length === 0} className="gap-1.5">
                {savingRoles ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save roles
              </Button>
            </div>
          </CardContent>
        ) : null}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={confirmRemove}
        title="Remove member"
        message={removing ? `Remove ${removing.email} from ${orgName}? Their account and other organizations are not affected.` : ''}
        confirmText={removeBusy ? 'Removing…' : 'Remove'}
      />
    </div>
  );
}

export default OrganizationMembers;
